import { useState, useCallback } from 'react';
import { apiFetch } from '../../../utils/apiAdmin';

const useAdminTaskOperations = (
    leadId,
    project,
    setTasks,
    fetchClientDashboard
) => {
    const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);
    const [loadingTaskDetail, setLoadingTaskDetail] = useState(false);
    const [savingChanges, setSavingChanges] = useState(false);
    const [savingField, setSavingField] = useState(null);
    const [creatingTask, setCreatingTask] = useState(false);
    const [creatingSubtask, setCreatingSubtask] = useState(false);

    const fetchTaskDetail = useCallback(async (taskId, showLoading = true) => {
        if (showLoading) {
            setLoadingTaskDetail(true);
        }
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(taskId)}/details?leadId=${leadId}`
            );
            if (response.ok) {
                const taskData = await response.json();
                setSelectedTaskDetail(prev => {
                    if (!showLoading && prev === null) {
                        return null;
                    }
                    return taskData;
                });
                return taskData;
            } else {
                throw new Error('Failed to load task details');
            }
        } catch (err) {
            console.error('Task detail fetch error:', err);
            if (showLoading) {
                throw err;
            }
            return null;
        } finally {
            if (showLoading) {
                setLoadingTaskDetail(false);
            }
        }
    }, [leadId]);

    const handleSaveChanges = useCallback(async (taskId, dataToSave, field = null) => {
        setSavingChanges(true);
        setSavingField(field);
        try {
            let updates = { ...dataToSave };
            // Project window override travels as a top-level request-control flag,
            // never as task data (Bill, 2026-08-03)
            const allowBeforeKickoff = updates.allowBeforeKickoff;
            delete updates.allowBeforeKickoff;
            if ('start' in updates) {
                updates.custom_attributes_values = {
                    ...(updates.custom_attributes_values || {}),
                    'Start Date': updates.start
                };
                delete updates.start;
            }
            if ('isMeeting' in updates) {
                updates.custom_attributes_values = {
                    ...(updates.custom_attributes_values || {}),
                    'Is Meeting': updates.isMeeting
                };
                delete updates.isMeeting;
            }
            if ('meetingType' in updates) {
                updates.custom_attributes_values = {
                    ...(updates.custom_attributes_values || {}),
                    'Meeting Type': updates.meetingType
                };
                delete updates.meetingType;
            }
            if ('agenda' in updates) {
                updates.custom_attributes_values = {
                    ...(updates.custom_attributes_values || {}),
                    'Agenda': updates.agenda
                };
                delete updates.agenda;
            }

            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(taskId)}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        updates: updates,
                        isSubtask: dataToSave.isSubtask || false,
                        projectId: project?.projectId,
                        leadId,
                        allowBeforeKickoff: allowBeforeKickoff
                    })
                }
            );
            if (response.ok) {
                // Trigger WebSocket refresh for connected clients
                apiFetch(
                    `${process.env.REACT_APP_API_URL}/admin/batch-signal/${leadId}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ signal: 'project_updated' })
                    }
                ).catch(e => console.warn('Failed to send update signal:', e));

                await fetchClientDashboard();
                // Also refresh task detail to update the modal
                await fetchTaskDetail(taskId, false);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to save changes');
            }
        } catch (err) {
            console.error('Save error:', err);
            throw err;
        } finally {
            setSavingChanges(false);
            setSavingField(null);
        }
    }, [project, fetchClientDashboard, fetchTaskDetail, leadId]);

    const handleDeleteTask = useCallback(async (taskId) => {
        if (!window.confirm('Are you sure you want to delete this task? This cannot be undone.')) {
            return;
        }

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(taskId)}`,
                {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ projectId: project?.projectId, leadId })
                }
            );

            if (response.ok) {
                // Handle both string and numeric IDs
                if (selectedTaskDetail && String(selectedTaskDetail.taskId) === String(taskId)) {
                    if (selectedTaskDetail.mainTaskId) {
                        await fetchTaskDetail(selectedTaskDetail.mainTaskId, false);
                    } else {
                        setSelectedTaskDetail(null);
                    }
                }
                await fetchClientDashboard();
            } else {
                throw new Error('Failed to delete task');
            }
        } catch (err) {
            console.error('Delete task error:', err);
            throw err;
        }
    }, [project, selectedTaskDetail, fetchTaskDetail, fetchClientDashboard]);

    const handleCreateTask = useCallback(async (taskData, swimlaneId) => {
        setCreatingTask(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        subject: taskData.subject,
                        description: taskData.description,
                        projectId: project.projectId,
                        swimlaneId: swimlaneId,
                        assignedUsers: taskData.assignedUsers || [],
                        assignedUsersNames: taskData.assignedUsersNames || [],
                        assignedToName: taskData.assignedToName || null
                    })
                }
            );

            if (response.ok) {
                await fetchClientDashboard();
            } else {
                const error = await response.json();
                throw new Error(error.error || 'Failed to create task');
            }
        } catch (err) {
            console.error('Create task error:', err);
            throw err;
        } finally {
            setCreatingTask(false);
        }
    }, [project, fetchClientDashboard]);

    const handleCreateSubtask = useCallback(async (parentTaskId, subtaskData, swimlaneId) => {
        setCreatingSubtask(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        subject: subtaskData.subject,
                        description: subtaskData.description,
                        projectId: project?.projectId,
                        swimlaneId: swimlaneId,
                        mainTaskId: parentTaskId,
                        assignedUsers: subtaskData.assignedUsers || [],
                        assignedToName: subtaskData.assignedToName || null,
                        start: subtaskData.start,
                        due_date: subtaskData.dueDate,
                        allowBeforeKickoff: subtaskData.allowBeforeKickoff,
                        isMeeting: subtaskData.isMeeting || false,
                        meetingType: subtaskData.meetingType || '',
                        agenda: subtaskData.agenda || ''
                    })
                }
            );

            if (response.ok) {
                await fetchTaskDetail(parentTaskId, false);
                await fetchClientDashboard();
            } else {
                throw new Error('Failed to create subtask');
            }
        } catch (err) {
            console.error('Subtask creation error:', err);
            throw err;
        } finally {
            setCreatingSubtask(false);
        }
    }, [project, fetchTaskDetail, fetchClientDashboard]);

    const handleDrop = useCallback(async (draggedTask, statusId, columns, updateInProgressRef) => {
        if (!draggedTask) return;

        updateInProgressRef.current = true;

        const newStatusInfo = columns.find(col => col.id === statusId);

        // Optimistic update (handle both string and numeric IDs)
        setTasks(prevTasks =>
            prevTasks.map(task =>
                String(task.taskId) === String(draggedTask.taskId)
                    ? {
                        ...task,
                        status: statusId,
                        statusName: newStatusInfo?.name || task.statusName
                    }
                    : task
            )
        );

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/admin/task/${encodeURIComponent(draggedTask.taskId)}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        updates: { status: statusId },
                        isSubtask: false,
                        projectId: project?.projectId,
                        leadId
                    })
                }
            );

            if (!response.ok) {
                // Revert on failure (handle both string and numeric IDs)
                setTasks(prevTasks =>
                    prevTasks.map(task =>
                        String(task.taskId) === String(draggedTask.taskId) ? draggedTask : task
                    )
                );
                throw new Error('Failed to update task');
            }

            setTimeout(() => {
                updateInProgressRef.current = false;
            }, 1000);

        } catch (err) {
            console.error('Update error:', err);
            // Revert on error (handle both string and numeric IDs)
            setTasks(prevTasks =>
                prevTasks.map(task =>
                    String(task.taskId) === String(draggedTask.taskId) ? draggedTask : task
                )
            );
            updateInProgressRef.current = false;
            throw err;
        }
    }, [project, setTasks]);

    return {
        selectedTaskDetail,
        setSelectedTaskDetail,
        loadingTaskDetail,
        savingChanges,
        savingField,
        creatingTask,
        creatingSubtask,
        fetchTaskDetail,
        handleSaveChanges,
        handleDeleteTask,
        handleCreateTask,
        handleCreateSubtask,
        handleDrop
    };
};

export default useAdminTaskOperations;
